import { takeLatest } from 'redux-saga/effects';
import {
  GET_AUTHORIZATION_INFO_FAILED,
  KEA_SIGN_IN_FAILED,
  KEA_SIGN_IN_WITH_SECRET_FAILED,
} from 'constants/ActionTypes';

import {
  GET_ALERTS_FAILED,
  GET_ALL_SETTINGS_FAILED,
  GET_DESCRIBES_FAILED,
  GET_PLATFORM_FAILED,
  SAVE_RECS_SETTINGS_FAILED,
  SAVE_SEARCH_SETTINGS_FAILED,
} from '../constants/actionTypes/settings';
import {
  GET_PRODUCT_BY_ID_FAILED,
  GET_PRODUCTS_FAILED,
} from '../constants/actionTypes/catalog';
import { GET_PLANS_FAILED } from '../constants/actionTypes/billing';
import * as search from '../constants/actionTypes/search';
import * as analytics from '../constants/actionTypes/analytics';
import * as recs from '../constants/actionTypes/recs';
import { getLocalizedMessage } from '../util/useLocalizedNotifications';

const isDev = window.APP_ENV === 'development' || !window.APP_ENV;
const isDebug = window.APP_ENV === 'debug';

function getErrorMessage(error) {
  if (!error) return getLocalizedMessage('errors.unknownWithoutObject');

  // Handle StatusCodeError from request-promise
  if (error.name === 'StatusCodeError') {
    const statusCode = error.statusCode || error.status || 'unknown';
    let details = '';

    if (error.error) {
      if (typeof error.error === 'string') {
        try {
          const parsed = JSON.parse(error.error);
          details = parsed.message || parsed.error || error.error;
        } catch (e) {
          details = error.error;
        }
      } else if (typeof error.error === 'object') {
        details = error.error.message || error.error.error || JSON.stringify(error.error);
      }
    }

    return getLocalizedMessage('errors.http', {
      status: statusCode,
      details: details || error.message || getLocalizedMessage('errors.server'),
    });
  }

  // Handle standard Error objects
  if (error instanceof Error) return error.message;

  // Handle plain objects with message/error properties
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  if (error.error) {
    if (typeof error.error === 'string') return error.error;
    if (error.error.message) return error.error.message;
  }

  // Fallback to JSON stringify
  try {
    return JSON.stringify(error);
  } catch (e) {
    return getLocalizedMessage('errors.notSerializable');
  }
}

function formatErrorForConsole(error) {
  if (!error) return 'No error object';

  const parts = [];

  // StatusCodeError details
  if (error.name === 'StatusCodeError') {
    parts.push(`Status: ${error.statusCode || 'unknown'}`);
    parts.push(`Method: ${error.options?.method || 'unknown'}`);
    parts.push(`URL: ${error.options?.uri || error.options?.url || 'unknown'}`);

    if (error.error) {
      parts.push(`Response: ${typeof error.error === 'string' ? error.error : JSON.stringify(error.error, null, 2)}`);
    }
  }

  return parts.length > 0 ? parts.join('\n') : JSON.stringify(error, null, 2);
}

function criticalErrorHandler(action) {
  const { payload: error } = action;
  const message = getErrorMessage(error);

  if (isDev) {
    console.group('🔴 Critical Error');
    console.error(message);
    console.log(formatErrorForConsole(error));
    console.groupEnd();
  } else if (isDebug) {
    console.error('Critical error:', error);
  }
  // TODO: add sentry in feature;
}

function errorHandler(action) {
  const { payload: error } = action;
  const message = getErrorMessage(error);

  if (isDev) {
    console.group('⚠️ Error');
    console.error(message);
    console.log(formatErrorForConsole(error));
    console.groupEnd();
  } else if (isDebug) {
    console.error('Error:', error);
  }
  // TODO: add sentry in feature;
}

export default function* errorHandlers() {
  yield takeLatest(GET_ALL_SETTINGS_FAILED, criticalErrorHandler);
  yield takeLatest(GET_AUTHORIZATION_INFO_FAILED, criticalErrorHandler);

  yield takeLatest(KEA_SIGN_IN_FAILED, errorHandler);
  yield takeLatest(KEA_SIGN_IN_WITH_SECRET_FAILED, errorHandler);

  yield takeLatest(SAVE_SEARCH_SETTINGS_FAILED, errorHandler);
  yield takeLatest(SAVE_RECS_SETTINGS_FAILED, errorHandler);
  yield takeLatest(search.SAVE_FIELDS_CHANGES_FAILED, errorHandler);
  yield takeLatest(recs.SAVE_FIELDS_CHANGES_FAILED, errorHandler);
  yield takeLatest(recs.RECS_UPDATE_ONE_WIDGET_FAILED, errorHandler);
  yield takeLatest(recs.RECS_DELETE_WIDGET_FAILED, errorHandler);
  yield takeLatest(recs.RECS_CREATE_WIDGET_FAILED, errorHandler);
  yield takeLatest(recs.RECS_UPDATE_PAGE_FAILED, errorHandler);
  yield takeLatest(GET_DESCRIBES_FAILED, errorHandler);
  yield takeLatest(GET_PLATFORM_FAILED, errorHandler);
  yield takeLatest(GET_ALERTS_FAILED, errorHandler);
  yield takeLatest(search.GET_FIELDS_FAILED, errorHandler);
  yield takeLatest(recs.GET_FIELDS_FAILED, errorHandler);
  yield takeLatest(recs.RECS_LOAD_ALL_MODELS_FAILED, errorHandler);
  yield takeLatest(recs.RECS_LOAD_ALL_WIDGETS_FAILED, errorHandler);
  yield takeLatest(recs.RECS_LOAD_ALL_PAGES_FAILED, errorHandler);
  yield takeLatest(GET_PRODUCT_BY_ID_FAILED, errorHandler);
  yield takeLatest(GET_PRODUCTS_FAILED, errorHandler);
  yield takeLatest(GET_PLANS_FAILED, errorHandler);
  yield takeLatest(analytics.GET_A_B_RESULTS_FAILED, errorHandler);
  yield takeLatest(analytics.GET_RECS_DASHBOARD_FAILED, errorHandler);
  yield takeLatest(analytics.GET_SEARCH_DASHBOARD_FAILED, errorHandler);
  yield takeLatest(analytics.GET_SEARCH_STATS_FAILED, errorHandler);
  yield takeLatest(analytics.GET_SEARCH_FILTER_POPULAR_FAILED, errorHandler);
  yield takeLatest(analytics.GET_SEARCH_LOST_OPPORTUNITIES_FAILED, errorHandler);
  yield takeLatest(analytics.GET_SEARCH_CURVE_FAILED, errorHandler);
  yield takeLatest(analytics.GET_SEARCH_QUERY_POPULAR_FAILED, errorHandler);
  yield takeLatest(analytics.GET_SEARCH_QUERY_SORTED_FAILED, errorHandler);
  yield takeLatest(analytics.GET_SEARCH_QUERY_NO_RESULT_FAILED, errorHandler);
  yield takeLatest(analytics.GET_SEARCH_RELATED_QUERIES_FAILED, errorHandler);
  yield takeLatest(analytics.GET_PRODUCTS_CONVERSION_BEST_FAILED, errorHandler);
  yield takeLatest(analytics.GET_PRODUCTS_CONVERSION_WORST_FAILED, errorHandler);
  yield takeLatest(analytics.GET_PRODUCTS_POPULAR_BY_VIEW_FAILED, errorHandler);
  yield takeLatest(analytics.GET_PRODUCTS_POPULAR_BY_PURCHASE_FAILED, errorHandler);
  yield takeLatest(analytics.GET_PRODUCTS_RELATED_BY_VIEW_FAILED, errorHandler);
  yield takeLatest(analytics.GET_PRODUCTS_RELATED_BY_PURCHASE_FAILED, errorHandler);
  yield takeLatest(analytics.GET_RELATED_PRODUCTS_BY_QUERY_FAILED, errorHandler);
  yield takeLatest(analytics.GET_RELATED_QUERIES_BY_PRODUCT_FAILED, errorHandler);
}
